import React, { useState, useEffect } from 'react';
import { Col, Row, Card, CardBody } from 'reactstrap';
import { OrderMenu } from './OrderMenu';
import OrderManagementService from '../../../Services/QRCodeServices/Order Management/OrderManagementService';
import { Triangle } from 'react-loader-spinner';

export const TabbedOrderPage = () => {
  const [activeTab, setActiveTab] = useState('orderList');
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [showImages, setShowImages] = useState(true);
  const maxImagesToShow = 5;

  const toggleExpandImages = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const toggleShowImages = () => {
    setShowImages(!showImages);
    setExpandedOrderId(null);
  };

  const fetchRecentOrders = async () => {
    setLoading(true);
    try {
      const response = await OrderManagementService.getOrder();
      const sortedOrders = response
        .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))
        .slice(0, 5);
      setRecentOrders(sortedOrders);
    } catch (error) {
      console.error("Error fetching recent orders:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentOrders();
    const interval = setInterval(fetchRecentOrders, 120000);
    return () => clearInterval(interval);
  }, []);

  const statusColors = {
    Ongoing: "#FFA500",
    Billing: "#655bd1",
    Cancelled: "#FF0000",
    Completed: "#15c46a",
  };

  return (
    <div className="container-fluid">
      {/* Tabs */}
      <div className="d-flex mb-4 border-bottom">
        <div
          className={`px-4 py-2 cursor-pointer ${
            activeTab === 'orderList' ? 'border-bottom rounded fw-bold bg-primary text-white border-3 border-primary' : 'transparent'
          }`}
          onClick={() => setActiveTab('orderList')}
        >
          Latest Orders
        </div>
        <div
          className={`px-4 py-2 cursor-pointer ${
            activeTab === 'orders' ? 'border-bottom rounded fw-bold border-3 text-white bg-primary border-primary' : 'transparent'
          }`}
          onClick={() => setActiveTab('orders')}
        >
          Add Order
        </div>
      </div>

      {/* Content */}
      <div className="tab-content">
        {activeTab === 'orders' ? (
          <OrderMenu />
        ) : (
          <div>
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <Triangle
                  visible={true}
                  height="80"
                  width="80"
                  color="#5B71B9"
                  ariaLabel="triangle-loading"
                />
              </div>
            ) : (
              <Row>
                {recentOrders.map((order) => {
                  const totalImages = order.menuOrderDetails?.length || 0;
                  const isExpanded = expandedOrderId === order.id;
                  const imagesToShow = isExpanded 
                    ? order.menuOrderDetails 
                    : order.menuOrderDetails?.slice(0, maxImagesToShow) || [];
                  const remainingImages = totalImages - maxImagesToShow;

                  return (
                    <Col key={order.id} md="12">
                      <Card
                        className="h-auto p-0"
                        style={{
                          borderWidth: '0px',
                          borderLeftWidth: '4px',
                          borderLeftColor: statusColors[order.billingStatus],
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}
                      >
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="card-title mb-0">
                              <span className="text-primary">{order.orderRef}</span>
                            </h5>
                            <span
                              className="badge rounded"
                              style={{
                                backgroundColor: statusColors[order.billingStatus],
                                color: '#fff',
                              }}
                            >
                              {order.billingStatus}
                            </span>
                          </div>

                          <div className="mb-3">
                            <Row className="mb-1">
                              <Col xs="6" className="text-muted">Total Amount:</Col>
                              <Col xs="6" className="text-end fw-medium">{order.totalAmount}</Col>
                            </Row>
                            <Row className="mb-1">
                              <Col xs="6" className="text-muted">Order Date:</Col>
                              <Col xs="6" className="text-end">
                                {new Date(order.createdDate).toLocaleDateString()}
                              </Col>
                            </Row>
                            <Row>
                              <Col xs="12">
                                {showImages && (
                                  <div className="d-flex align-items-center">
                                    <div 
                                      className="d-flex"
                                      style={{ 
                                        marginRight: isExpanded ? '10px' : '0px'
                                      }}
                                    >
                                      {imagesToShow.map((detail, index) => (
                                        <div
                                          key={index}
                                          className='bg-light relative'
                                          style={{
                                            width: "32px",
                                            height: "32px",
                                            marginLeft: index === 0 ? '0' : '-8px',
                                            zIndex: imagesToShow.length - index,
                                            borderRadius: '50%',
                                            overflow: 'hidden',
                                            border: '2px solid #fff',
                                          }}
                                        >
                                          <img
                                            src={detail.menuItemDetail.cloudR2ImagePath?.getCDNUrl()}
                                            alt={`Product ${index + 1}`}
                                            style={{ 
                                              width: '100%',
                                              height: '100%',
                                              objectFit: 'cover'
                                            }}
                                          />
                                        </div>
                                      ))}
                                      {remainingImages > 0 && !isExpanded && (
                                        <div
                                          className='bg-light relative'
                                          style={{
                                            width: "32px",
                                            height: "32px",
                                            marginLeft: '-8px',
                                            zIndex: 0,
                                            borderRadius: '50%',
                                            border: '2px solid #fff',
                                            backgroundColor: '#000',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                          }}
                                          onClick={() => toggleExpandImages(order.id)}
                                        >
                                          <span className="text-muted">+{remainingImages}</span>
                                        </div>
                                      )}
                                    </div>
                                    {isExpanded && (
                                      <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => toggleExpandImages(order.id)}
                                      >
                                        Hide
                                      </button>
                                    )}
                                  </div>
                                )}
                              </Col>
                            </Row>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </div>
        )}
      </div>
    </div>
  );
};